import type { Product, VariantGroup } from '../types/product';
import { IMG } from './images';

const installGroup: VariantGroup = {
  id: 'install',
  name: 'Installation',
  kind: 'card',
  options: [
    { id: 'self', label: 'I have my own installer', detail: 'Delivered to your door, ready to mount', priceDelta: 0 },
    { id: 'pro', label: 'Certified Ampra installer', detail: 'Fitted & commissioned within 3 weeks', priceDelta: 749 },
  ],
};

export const products: Product[] = [
  // ——— Home batteries (configurator template)
  {
    id: 'ampra-stack',
    name: 'Ampra Stack Home Battery',
    brand: 'Ampra',
    category: 'home-batteries',
    template: 'system',
    image: IMG.home,
    gallery: [IMG.home, IMG.homeDark, IMG.hero],
    price: 2649,
    compareAt: 2949,
    rating: 4.9,
    reviews: 412,
    specs: ['5.1–15.3 kWh', 'Modular'],
    badge: 'Bestseller',
    stock: 'in-stock',
    capacityWh: 5100,
    summary:
      'A modular LFP home battery that grows with you. Start with two modules and add more later — each one clicks on in minutes, no rewiring needed.',
    highlights: ['LiFePO₄ cells, 8,000 cycles', 'Expand from 5.1 to 15.3 kWh', 'Backup power within 10 ms', '10-year warranty'],
    specTable: [
      { label: 'Cell chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Capacity per module', value: '2.56 kWh' },
      { label: 'Max. modules', value: '6' },
      { label: 'Cycle life', value: '8,000 @ 90% DoD' },
      { label: 'IP rating', value: 'IP65 (indoor & outdoor)' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capacity',
        kind: 'card',
        options: [
          { id: '5', label: '5.1 kWh', detail: '2 modules · small homes', priceDelta: 0, capacityWh: 5100 },
          { id: '7', label: '7.7 kWh', detail: '3 modules · 2–3 persons', priceDelta: 700, capacityWh: 7700 },
          { id: '10', label: '10.2 kWh', detail: '4 modules · most popular', priceDelta: 1350, capacityWh: 10200 },
          { id: '15', label: '15.3 kWh', detail: '6 modules · heat pump / EV', priceDelta: 2650, capacityWh: 15300 },
        ],
      },
      {
        id: 'color',
        name: 'Finish',
        kind: 'swatch',
        options: [
          { id: 'white', label: 'Arctic white', swatch: '#EEF1F0', priceDelta: 0, image: IMG.home },
          { id: 'anthracite', label: 'Anthracite', swatch: '#2E3438', priceDelta: 0, image: IMG.homeDark },
        ],
      },
      {
        id: 'inverter',
        name: 'Hybrid inverter',
        kind: 'card',
        options: [
          { id: 'none', label: 'Use my existing inverter', detail: 'Compatible hybrid inverter required', priceDelta: 0 },
          { id: 'h5', label: 'Ampra Hybrid 5 kW', detail: 'Up to 12 panels', priceDelta: 1190 },
          { id: 'h8', label: 'Ampra Hybrid 8 kW', detail: 'Up to 20 panels, 3-phase', priceDelta: 1590 },
        ],
      },
      installGroup,
    ],
    bundleIds: ['homewizard-p1', 'mc4-kit'],
  },
  {
    id: 'alphaess-vitapower',
    name: 'AlphaESS VitaPower Battery',
    brand: 'AlphaESS',
    category: 'home-batteries',
    template: 'system',
    image: IMG.homeDark,
    gallery: [IMG.homeDark, IMG.home],
    price: 2149,
    rating: 4.7,
    reviews: 96,
    specs: ['8–16 kWh', '3.68 kW'],
    stock: 'in-stock',
    capacityWh: 8000,
    summary: 'Plug-and-play home storage with an integrated inverter. Connects to a regular wall socket — ideal for retrofit.',
    highlights: ['Integrated 3.68 kW inverter', 'IP65 indoor/outdoor', 'App with dynamic tariffs', '10-year warranty'],
    specTable: [
      { label: 'Cell chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Continuous power', value: '3,680 W' },
      { label: 'IP rating', value: 'IP65' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capacity',
        kind: 'card',
        options: [
          { id: '8', label: '8 kWh', detail: '2 modules', priceDelta: 0, capacityWh: 8000 },
          { id: '12', label: '12 kWh', detail: '3 modules', priceDelta: 990, capacityWh: 12000 },
          { id: '16', label: '16 kWh', detail: '4 modules', priceDelta: 1980, capacityWh: 16000 },
        ],
      },
      installGroup,
    ],
    bundleIds: ['homewizard-p1'],
  },
  {
    id: 'huawei-luna2000',
    name: 'Huawei LUNA2000 Smart Battery',
    brand: 'Huawei',
    category: 'home-batteries',
    template: 'system',
    image: IMG.home,
    gallery: [IMG.home, IMG.hero],
    price: 2390,
    compareAt: 2590,
    rating: 4.8,
    reviews: 221,
    specs: ['5–15 kWh', '2.5 kW/module'],
    stock: 'low',
    capacityWh: 5000,
    summary: 'The go-to battery for Huawei SUN2000 inverters. Module-level optimisation means every kWh is used.',
    highlights: ['Module-level energy optimisation', 'Works with SUN2000 inverters', '10-year warranty'],
    specTable: [
      { label: 'Cell chemistry', value: 'LiFePO₄ (LFP)' },
      { label: 'Capacity per module', value: '5 kWh' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'capacity',
        name: 'Capacity',
        kind: 'card',
        options: [
          { id: '5', label: '5 kWh', detail: '1 module', priceDelta: 0, capacityWh: 5000 },
          { id: '10', label: '10 kWh', detail: '2 modules', priceDelta: 1790, capacityWh: 10000 },
          { id: '15', label: '15 kWh', detail: '3 modules', priceDelta: 3580, capacityWh: 15000 },
        ],
      },
      installGroup,
    ],
  },

  // ——— Plug-in batteries
  {
    id: 'zendure-solarflow-2400',
    name: 'Zendure SolarFlow 2400 AC',
    brand: 'Zendure',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 899,
    rating: 4.7,
    reviews: 238,
    specs: ['2.4 kWh', '2400 W'],
    badge: 'New',
    stock: 'in-stock',
    capacityWh: 2400,
    summary: 'Plug into any socket and store surplus balcony solar. Charges from the grid at night when prices are lowest.',
    highlights: ['Plug & play, no installer', 'Dynamic tariff charging', 'Expand to 16.8 kWh', 'IP65'],
    specTable: [
      { label: 'Capacity', value: '2,400 Wh' },
      { label: 'AC output', value: '2,400 W' },
      { label: 'Solar input', value: '4 × MPPT, 3,000 W' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'expansion',
        name: 'Extra battery',
        kind: 'pill',
        options: [
          { id: 'none', label: 'None', priceDelta: 0, capacityWh: 2400 },
          { id: '1', label: '+1 AB3000 (2.9 kWh)', priceDelta: 699, capacityWh: 5280 },
          { id: '2', label: '+2 AB3000 (5.8 kWh)', priceDelta: 1349, capacityWh: 8160 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460', 'homewizard-p1'],
  },
  {
    id: 'anker-solarbank-3',
    name: 'Anker SOLIX Solarbank 3 Pro',
    brand: 'Anker SOLIX',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 1099,
    compareAt: 1299,
    rating: 4.6,
    reviews: 187,
    specs: ['2.7 kWh', '1200 W'],
    stock: 'in-stock',
    capacityWh: 2700,
    summary: 'Smart balcony storage with AI-driven tariff planning and a sleek, weatherproof design.',
    highlights: ['AI energy planning', 'Up to 16 kWh expansion', 'IP65 weatherproof'],
    specTable: [
      { label: 'Capacity', value: '2,688 Wh' },
      { label: 'AC output', value: '1,200 W' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'expansion',
        name: 'Extra battery',
        kind: 'pill',
        options: [
          { id: 'none', label: 'None', priceDelta: 0, capacityWh: 2700 },
          { id: '1', label: '+1 BP2700', priceDelta: 749, capacityWh: 5400 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460'],
  },
  {
    id: 'ampra-plug',
    name: 'Ampra Plug 2 Balcony Battery',
    brand: 'Ampra',
    category: 'plug-in',
    template: 'standard',
    image: IMG.plugin,
    gallery: [IMG.plugin, IMG.pluginLife],
    price: 649,
    rating: 4.5,
    reviews: 74,
    specs: ['1.9 kWh', '800 W'],
    badge: 'New',
    stock: 'in-stock',
    capacityWh: 1920,
    summary: 'Our most affordable way to store balcony solar. Compact, silent, set up in 10 minutes.',
    highlights: ['Set up in 10 minutes', 'Silent, fanless', '5-year warranty'],
    specTable: [
      { label: 'Capacity', value: '1,920 Wh' },
      { label: 'AC output', value: '800 W' },
      { label: 'Warranty', value: '5 years' },
    ],
    variants: [
      {
        id: 'color',
        name: 'Colour',
        kind: 'swatch',
        options: [
          { id: 'white', label: 'Arctic white', swatch: '#EEF1F0', priceDelta: 0 },
          { id: 'anthracite', label: 'Anthracite', swatch: '#2E3438', priceDelta: 20 },
        ],
      },
    ],
    bundleIds: ['aiko-neostar-460', 'mc4-kit'],
  },

  // ——— Power stations
  {
    id: 'ecoflow-delta-3-plus',
    name: 'EcoFlow DELTA 3 Plus Power Station',
    brand: 'EcoFlow',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 699,
    compareAt: 899,
    rating: 4.8,
    reviews: 1024,
    specs: ['1024 Wh', '1800 W'],
    stock: 'low',
    capacityWh: 1024,
    summary: 'Fast-charging portable power for camping, vans and home backup. 0–100% in 56 minutes from the wall.',
    highlights: ['0–100% in 56 min', 'UPS switchover < 10 ms', '13 outputs', 'Expandable to 5 kWh'],
    specTable: [
      { label: 'Capacity', value: '1,024 Wh' },
      { label: 'AC output', value: '1,800 W (surge 3,600 W)' },
      { label: 'Weight', value: '12.5 kg' },
      { label: 'Warranty', value: '5 years' },
    ],
    variants: [
      {
        id: 'bundle',
        name: 'Bundle',
        kind: 'pill',
        options: [
          { id: 'unit', label: 'Unit only', priceDelta: 0, capacityWh: 1024 },
          { id: 'solar', label: '+ 220 W solar panel', priceDelta: 349, capacityWh: 1024 },
          { id: 'battery', label: '+ Extra battery', priceDelta: 599, capacityWh: 2048 },
        ],
      },
    ],
    bundleIds: ['mc4-kit', 'ecoflow-river-3'],
  },
  {
    id: 'bluetti-ac180',
    name: 'BLUETTI AC180 Portable Power Station',
    brand: 'BLUETTI',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 549,
    rating: 4.6,
    reviews: 640,
    specs: ['1152 Wh', '1800 W'],
    stock: 'in-stock',
    capacityWh: 1152,
    summary: 'Reliable all-rounder with LFP cells and 3,500+ cycles. A favourite for campers and vanlifers.',
    highlights: ['LiFePO₄, 3,500+ cycles', '1,800 W output', 'App control'],
    specTable: [
      { label: 'Capacity', value: '1,152 Wh' },
      { label: 'AC output', value: '1,800 W' },
      { label: 'Weight', value: '16 kg' },
      { label: 'Warranty', value: '5 years' },
    ],
    variants: [
      {
        id: 'bundle',
        name: 'Bundle',
        kind: 'pill',
        options: [
          { id: 'unit', label: 'Unit only', priceDelta: 0 },
          { id: 'solar', label: '+ 200 W solar panel', priceDelta: 299 },
        ],
      },
    ],
    bundleIds: ['mc4-kit'],
  },
  {
    id: 'ecoflow-river-3',
    name: 'EcoFlow RIVER 3 Power Station',
    brand: 'EcoFlow',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 199,
    rating: 4.5,
    reviews: 312,
    specs: ['245 Wh', '300 W'],
    badge: 'New',
    stock: 'in-stock',
    capacityWh: 245,
    summary: 'Pocketable power for weekends away. Charges laptops, cameras and lights.',
    highlights: ['Just 3.5 kg', 'Silent X-Boost 600 W', 'Full charge in 1 hour'],
    specTable: [
      { label: 'Capacity', value: '245 Wh' },
      { label: 'AC output', value: '300 W' },
      { label: 'Weight', value: '3.5 kg' },
    ],
  },
  {
    id: 'anker-solix-c1000',
    name: 'Anker SOLIX C1000 Power Station',
    brand: 'Anker SOLIX',
    category: 'power-stations',
    template: 'standard',
    image: IMG.station,
    gallery: [IMG.station, IMG.stationLife],
    price: 449,
    compareAt: 599,
    rating: 4.7,
    reviews: 805,
    specs: ['1056 Wh', '1800 W'],
    stock: 'in-stock',
    capacityWh: 1056,
    summary: 'Compact 1 kWh power station with UltraFast charging and a built-in light.',
    highlights: ['58-min full recharge', 'Built-in camping light', '5-year warranty'],
    specTable: [
      { label: 'Capacity', value: '1,056 Wh' },
      { label: 'AC output', value: '1,800 W' },
      { label: 'Weight', value: '12.9 kg' },
    ],
  },

  // ——— Solar panels
  {
    id: 'aiko-neostar-460',
    name: 'Aiko Neostar 460 Wp Full Black',
    brand: 'Aiko',
    category: 'solar-panels',
    template: 'standard',
    image: IMG.solar,
    gallery: [IMG.solar, IMG.pluginLife],
    price: 119,
    compareAt: 139,
    rating: 4.8,
    reviews: 352,
    specs: ['460 Wp', '23.6%'],
    badge: 'Bestseller',
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'All-black back-contact panel with class-leading efficiency and a clean look on any roof.',
    highlights: ['23.6% efficiency', 'Full black design', '25-year product warranty'],
    specTable: [
      { label: 'Power', value: '460 Wp' },
      { label: 'Efficiency', value: '23.6%' },
      { label: 'Dimensions', value: '1762 × 1134 × 30 mm' },
      { label: 'Warranty', value: '25 years' },
    ],
    variants: [
      {
        id: 'pack',
        name: 'Pack size',
        kind: 'pill',
        options: [
          { id: '1', label: '1 panel', priceDelta: 0 },
          { id: '4', label: '4 panels', detail: '€112 each', priceDelta: 329 },
          { id: '10', label: '10 panels', detail: '€105 each', priceDelta: 931 },
        ],
      },
    ],
    bundleIds: ['mc4-kit'],
  },
  {
    id: 'jinko-tiger-neo-440',
    name: 'Jinko Tiger Neo 440 Wp',
    brand: 'Jinko',
    category: 'solar-panels',
    template: 'standard',
    image: IMG.solar,
    gallery: [IMG.solar],
    price: 99,
    rating: 4.6,
    reviews: 188,
    specs: ['440 Wp', '22.5%'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'N-type TOPCon panel with great value per watt and low degradation.',
    highlights: ['N-type TOPCon', 'Low degradation', '25-year warranty'],
    specTable: [
      { label: 'Power', value: '440 Wp' },
      { label: 'Efficiency', value: '22.5%' },
      { label: 'Warranty', value: '25 years' },
    ],
  },

  // ——— Inverters
  {
    id: 'ampra-hybrid',
    name: 'Ampra Hybrid Inverter',
    brand: 'Ampra',
    category: 'inverters',
    template: 'standard',
    image: IMG.inverter,
    gallery: [IMG.inverter, IMG.hero],
    price: 1190,
    rating: 4.8,
    reviews: 143,
    specs: ['5–10 kW', 'Hybrid'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Hybrid inverter that manages panels, battery and grid in one box. Pairs with Ampra Stack.',
    highlights: ['Battery-ready', 'Backup output', 'Wi-Fi monitoring'],
    specTable: [
      { label: 'Phases', value: '1 or 3' },
      { label: 'Efficiency', value: '97.6%' },
      { label: 'Warranty', value: '10 years' },
    ],
    variants: [
      {
        id: 'power',
        name: 'Power',
        kind: 'pill',
        options: [
          { id: '5', label: '5 kW', priceDelta: 0 },
          { id: '8', label: '8 kW', priceDelta: 400 },
          { id: '10', label: '10 kW', priceDelta: 790 },
        ],
      },
    ],
    bundleIds: ['homewizard-p1'],
  },
  {
    id: 'victron-multiplus-ii',
    name: 'Victron MultiPlus-II 48/5000',
    brand: 'Victron Energy',
    category: 'inverters',
    template: 'standard',
    image: IMG.inverter,
    gallery: [IMG.inverter],
    price: 1349,
    rating: 4.9,
    reviews: 67,
    specs: ['5 kVA', '48 V'],
    stock: 'preorder',
    capacityWh: 0,
    summary: 'Professional inverter/charger for off-grid and ESS systems.',
    highlights: ['Off-grid & ESS', 'Parallel up to 6 units', '5-year warranty'],
    specTable: [
      { label: 'Power', value: '5,000 VA' },
      { label: 'Battery voltage', value: '48 V' },
    ],
  },

  // ——— Accessories
  {
    id: 'homewizard-p1',
    name: 'HomeWizard P1 Energy Meter',
    brand: 'HomeWizard',
    category: 'accessories',
    template: 'standard',
    image: IMG.accessory,
    gallery: [IMG.accessory],
    price: 29.95,
    rating: 4.7,
    reviews: 1530,
    specs: ['Wi-Fi', 'P1 port'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Read your smart meter live. Required for zero-feed-in with most plug-in batteries.',
    highlights: ['Live usage per second', 'Works with Zendure & Anker'],
    specTable: [{ label: 'Connection', value: 'P1 port, Wi-Fi' }],
  },
  {
    id: 'mc4-kit',
    name: 'Solar Cable Kit with MC4',
    brand: 'Ampra',
    category: 'accessories',
    template: 'standard',
    image: IMG.accessory,
    gallery: [IMG.accessory],
    price: 24.95,
    rating: 4.6,
    reviews: 420,
    specs: ['6 mm²', 'UV-proof'],
    stock: 'in-stock',
    capacityWh: 0,
    summary: 'Pre-crimped red/black solar cable with MC4 connectors.',
    highlights: ['Pre-crimped', 'UV and weather resistant'],
    specTable: [{ label: 'Cross-section', value: '6 mm²' }],
    variants: [
      {
        id: 'length',
        name: 'Length',
        kind: 'pill',
        options: [
          { id: '5', label: '5 m', priceDelta: 0 },
          { id: '10', label: '10 m', priceDelta: 10 },
          { id: '25', label: '25 m', priceDelta: 30 },
        ],
      },
    ],
  },
];
